import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import type { Montagem } from '../lib/api'
import { useSessao } from '../lib/sessao'
import { ir } from '../lib/navegacao'
import { Carregando, Erro, Vazio } from '../componentes/ui'
import { brl, hora } from '../lib/formato'
import './inicio.css'

/**
 * A primeira tela depois de entrar.
 *
 * ─── POR QUE ELA ABRE COM UMA FRASE ───────────────────────────────
 *
 * A referência que o Lucas aprovou não abre com número: abre com
 * "Boa tarde! 50 pacientes hoje com 2 profissionais atendendo". Quatro
 * KPIs exigem que a pessoa interprete; uma oração já interpretou. Quem
 * chega às 8h lê a frase e sabe o dia — quem quer o detalhe desce os
 * olhos para a faixa.
 *
 * ─── OS TRÊS ESTADOS VAZIOS ───────────────────────────────────────
 *
 * Foi aqui que o Lucas travou ao criar a conta: clínica nova, tudo
 * zerado, nenhuma indicação do que fazer. São três situações
 * diferentes e cada uma pede outra coisa:
 *
 *  1. Clínica NOVA (sem sala, sem profissional, sem procedimento) →
 *     lista de partida, com a CONSEQUÊNCIA escrita em cada passo. "Sem
 *     sala cadastrada, a agenda não tem onde marcar" ensina mais que
 *     "cadastre uma sala".
 *  2. Clínica PRONTA, dia vazio → não é erro, é terça de manhã. Mostra
 *     amanhã e quem está esperando.
 *  3. Sem permissão para um pedaço → o cartão não é renderizado. A
 *     faixa fica com três, não com um buraco.
 */

type Agendamento = {
  id: string
  inicio: string
  fim: string
  status: string
  confirmacao: string | null
  paciente: string | null
  paciente_social: string | null
  procedimento: string | null
  alocacao: Array<{ profissional: string | null; recurso: string | null; principal: boolean }>
}

/* A rota da agenda casa por INTERSEÇÃO de intervalo
   (`a.inicio < $ate AND a.fim > $de`). Mandar `YYYY-MM-DD` vira
   meia-noite nos dois lados e a janela nasce de largura zero — a
   consulta responde 200 com lista vazia e parece que não há agenda.
   `Agenda.tsx` sempre mandou `toISOString()`; o Início precisa do
   mesmo contrato. */
const diaIso = (deslocamento = 0) => {
  const i = new Date(); i.setHours(0, 0, 0, 0); i.setDate(i.getDate() + deslocamento)
  const f = new Date(i); f.setDate(f.getDate() + 1)
  return { de: i.toISOString(), ate: f.toISOString(), data: i.toISOString().slice(0, 10) }
}

function saudacao(): string {
  const h = new Date().getHours()
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite'
}

/**
 * O primeiro nome basta — o cabeçalho não é um crachá.
 *
 * Pula o tratamento: metade dos profissionais de saúde cadastra o nome
 * como "Dra. Ana Beatriz", e "Boa tarde, Dra.." é pior que não saudar.
 */
const TRATAMENTOS = /^(dr|dra|sr|sra|prof|profa|me|dr\.ª)\.?$/i
const primeiroNome = (n: string | null) => {
  const partes = (n ?? '').trim().split(/\s+/).filter(Boolean)
  const primeiro = partes.find((p) => !TRATAMENTOS.test(p))
  return primeiro ?? partes[0] ?? ''
}

export function Inicio() {
  const { estado, pode } = useSessao()
  const nome = estado.fase === 'dentro' ? estado.ctx.nome : null

  const verAgenda = pode('agenda:read')
  const verDinheiro = pode('financeiro:read')

  const [hojeItens, setHojeItens] = useState<Agendamento[]>([])
  const [amanhaItens, setAmanhaItens] = useState<Agendamento[]>([])
  const [espera, setEspera] = useState(0)
  const [receber, setReceber] = useState<{ aberto_centavos: string; abertos: string } | null>(null)
  const [montagem, setMontagem] = useState<Montagem | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    const d = diaIso(0), a = diaIso(1)
    try {
      /* Tudo em paralelo e cada pedaço com o próprio `catch`: um 404 de
         escopo num cartão não pode derrubar a tela inteira. Foi assim
         que uma rota quebrada deixou o produto inteiro parecendo morto. */
      const nada = () => null
      const [ag, am, ep, fi, mont] = await Promise.all([
        verAgenda ? api<{ agendamentos: Agendamento[] }>(`/v1/agenda?de=${d.de}&ate=${d.ate}`).catch(nada) : null,
        verAgenda ? api<{ agendamentos: Agendamento[] }>(`/v1/agenda?de=${a.de}&ate=${a.ate}`).catch(nada) : null,
        verAgenda ? api<{ total: number }>('/v1/espera').catch(nada) : null,
        /* Campos PLANOS, e os números chegam como STRING (bigint do
           Postgres serializa assim). Tratar como number faria
           "0" somar como texto e a conta sair errada em silêncio. */
        verDinheiro ? api<{ aberto_centavos: string; abertos: string }>(
          `/v1/financeiro/resumo?de=${d.data}&ate=${d.data}`).catch(nada) : null,
        /* UMA chamada, respondida pelo banco.
           Eram CINCO listagens paralelas para descobrir se cada tabela
           tinha ao menos uma linha — o que trazia até 50 registros de
           cada uma só para ler `.length > 0`. E nenhuma delas sabia
           dizer se a IDENTIDADE da clínica estava preenchida, porque
           esse dado não existia até a 0036. */
        api<Montagem>('/v1/clinica/montagem').catch(nada),
      ])
      setHojeItens(ag?.agendamentos ?? [])
      setAmanhaItens(am?.agendamentos ?? [])
      setEspera(ep?.total ?? 0)
      setReceber(fi ?? null)
      setMontagem(mont ?? null)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar o painel.')
    } finally { setCarregando(false) }
  }, [verAgenda, verDinheiro])

  useEffect(() => { void carregar() }, [carregar])

  if (carregando) return <Carregando altura={260} />

  /* ── estado 1: a clínica ainda não existe de verdade ───────────── */
  /* O roteiro, com o destino EXATO de cada passo.
     Antes quatro dos cinco apontavam para `/ajustes` — a mesma tela de
     540 linhas com seis grupos — e a pessoa tinha de adivinhar em qual
     deles a tarefa morava. Agora cada tarefa tem endereço próprio.

     O texto do "porquê" é o que separa lista de tarefas de lista de
     CONSEQUÊNCIAS: "sem sala cadastrada, a agenda não tem onde marcar"
     diz por que vale o clique. */
  const RECEITA: Record<string, { rotulo: string; porque: string; onde: string; botao: string }> = {
    identidade: {
      rotulo: 'Diga quem é a clínica',
      porque: 'Endereço e telefone vão no recibo e na página de agendamento. Sem eles o paciente não pede reembolso ao convênio.',
      onde: '/identidade', botao: 'Preencher',
    },
    equipe: {
      rotulo: 'Cadastre quem atende',
      porque: 'Sem profissional, não há em nome de quem marcar.',
      onde: '/equipe', botao: 'Cadastrar profissional',
    },
    salas: {
      rotulo: 'Diga onde se atende',
      porque: 'Sem sala ou equipamento cadastrado, a agenda não tem onde encaixar o paciente.',
      onde: '/salas', botao: 'Cadastrar sala',
    },
    catalogo: {
      rotulo: 'Diga o que a clínica faz',
      porque: 'O procedimento define a duração e o preço de cada horário.',
      onde: '/catalogo', botao: 'Cadastrar procedimento',
    },
    horarios: {
      rotulo: 'Defina os horários de trabalho',
      porque: 'Sem jornada, a grade aceita marcação em qualquer hora — inclusive de madrugada.',
      onde: '/horarios', botao: 'Definir horários',
    },
  }

  const passos = (montagem?.passos ?? []).map((p) => ({
    ...RECEITA[p.id]!, pronto: p.pronto,
  }))

  /* O índice do primeiro passo em aberto — é ele que ganha a ação
     primária, e é ele que a frase de contexto nomeia. */
  const proximo = passos.findIndex((p) => !p.pronto)
  const feitos = montagem?.feitos ?? 0
  /* "Nova" enquanto FALTAR passo. Antes o corte era `feitos < 4`, o que
     declarava a clínica pronta com um passo em aberto — e o passo que
     sobrava costumava ser a jornada, justamente o que deixa marcar de
     madrugada. */
  const clinicaNova = passos.length > 0 && feitos < passos.length

  if (clinicaNova) {
    return (
      <div className="ini">
        <Erro mensagem={erro} />
        <header className="ini__cab">
          <h1>{saudacao()}, {primeiroNome(nome)}.</h1>
          {/* Frase, não faixa de números — §8 das referências. E ela
              NOMEIA o próximo passo: "faltam 5 passos" informa; "comece
              dizendo quem é a clínica" instrui. */}
          <p className="ini__frase">
            {feitos === 0
              ? <>Sua clínica ainda não está montada. Comece dizendo <strong>quem ela é</strong> — são {passos.length} passos, uns dez minutos.</>
              : <>Faltam {passos.length - feitos} {passos.length - feitos === 1 ? 'passo' : 'passos'} para a agenda funcionar. O próximo é <strong>{passos[proximo]?.rotulo.toLowerCase()}</strong>.</>}
          </p>
        </header>

        <section className="cartao ini__partida">
          <div className="ini__progresso" role="img"
               aria-label={`${feitos} de ${passos.length} passos concluídos`}>
            {passos.map((p, i) => (
              <span key={i} className={p.pronto ? 'is-pronto' : ''} />
            ))}
          </div>
          <ol className="ini__passos">
            {passos.map((p, i) => (
              <li key={i} className={p.pronto ? 'is-pronto' : ''}>
                <span className="ini__marca" aria-hidden="true">{p.pronto ? '✓' : i + 1}</span>
                <div className="ini__passo-txt">
                  <strong>{p.rotulo}</strong>
                  <span className="ajuda">{p.porque}</span>
                </div>
                {/* Só o PRÓXIMO passo aberto é ação primária.
                    Cinco botões sólidos competiam entre si e a tela
                    não dizia por onde começar — `docs/design-composicao.md`
                    §7: "uma única ação primária por campo visual, nunca
                    duas competindo". Os demais ficam em contorno:
                    presentes, alcançáveis, não convidativos. É o
                    `--fantasma` deste projeto, que já É o contorno da
                    hierarquia de quatro pesos das referências. */}
                {!p.pronto && (
                  <button className={`botao botao--pequeno${i === proximo ? '' : ' botao--fantasma'}`}
                          onClick={() => ir(p.onde)}>
                    {p.botao}
                  </button>
                )}
              </li>
            ))}
          </ol>
        </section>
      </div>
    )
  }

  /* ── estado 2 e 3: a clínica trabalha ───────────────────────────── */
  const atendendo = hojeItens.filter((a) => a.status === 'EM_ATENDIMENTO')
  const naRecepcao = hojeItens.filter((a) => a.status === 'NA_RECEPCAO')
  const profissionaisHoje = new Set(
    hojeItens.flatMap((a) => a.alocacao.filter((x) => x.principal).map((x) => x.profissional)),
  )
  profissionaisHoje.delete(null)

  const frase = [
    `${hojeItens.length} ${hojeItens.length === 1 ? 'atendimento' : 'atendimentos'} hoje`,
    profissionaisHoje.size > 0
      && `${profissionaisHoje.size} ${profissionaisHoje.size === 1 ? 'profissional' : 'profissionais'} na agenda`,
    naRecepcao.length > 0 && `${naRecepcao.length} na recepção agora`,
  ].filter(Boolean).join(' · ')

  return (
    <div className="ini">
      <Erro mensagem={erro} />

      <header className="ini__cab">
        <h1>{saudacao()}, {primeiroNome(nome)}.</h1>
        <p className="ini__frase">{frase}</p>
      </header>

      {/* A faixa. Cartão cujo escopo a pessoa não tem NÃO é renderizado —
          a faixa fica com três, não com um buraco cinza. */}
      <div className="ini__faixa">
        {verAgenda && (
          <Kpi rotulo="Hoje" glifo="▤" valor={String(hojeItens.length)}
               frase={atendendo.length > 0
                 ? `${atendendo.length} em atendimento agora`
                 : hojeItens.length > 0 ? 'nenhum em atendimento' : 'agenda livre'}
               aoAbrir={() => ir('/agenda')} />
        )}
        {verDinheiro && receber && (
          <Kpi rotulo="A receber" glifo="⊞" valor={brl(receber.aberto_centavos)}
               frase={Number(receber.abertos) > 0
                 ? `${receber.abertos} em aberto`
                 : 'nada em aberto'}
               aoAbrir={() => ir('/recebimentos')} />
        )}
        {verAgenda && (
          <Kpi rotulo="Lista de espera" glifo="⏲" valor={String(espera)}
               frase={espera > 0 ? 'esperando um horário' : 'ninguém esperando'}
               aoAbrir={() => ir('/agenda')} />
        )}
        {verAgenda && (
          <Kpi rotulo="Amanhã" glifo="→" valor={String(amanhaItens.length)}
               frase={amanhaItens.length > 0 ? 'já marcados' : 'nada marcado ainda'}
               aoAbrir={() => ir('/agenda')} />
        )}
      </div>

      {verAgenda && (
        <section className="cartao ini__dia">
          <header className="ini__dia-cab">
            <h2>O dia</h2>
            <button className="botao botao--fantasma botao--pequeno"
                    onClick={() => ir('/agenda')}>Abrir a agenda</button>
          </header>

          {hojeItens.length === 0 ? (
            <Vazio
              mensagem="Nenhum atendimento hoje."
              sugestao={amanhaItens.length > 0
                ? `Amanhã já tem ${amanhaItens.length}. ${espera > 0 ? `E ${espera} pessoa(s) esperam um horário.` : ''}`
                : espera > 0
                  ? `${espera} pessoa(s) esperam um horário — talvez caiba alguém hoje.`
                  : 'Dia livre. Bom para pôr o cadastro em dia.'}
            />
          ) : (
            <ul className="ini__lista">
              {hojeItens.slice(0, 8).map((a) => {
                const principal = a.alocacao.find((x) => x.principal)
                return (
                  <li key={a.id}>
                    <span className="ini__hora mono">{hora(a.inicio)}</span>
                    <div className="ini__quem">
                      <strong>{a.paciente_social || a.paciente || '—'}</strong>
                      <span className="ajuda">
                        {[a.procedimento, principal?.profissional, principal?.recurso]
                          .filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <EstadoDoDia status={a.status} confirmacao={a.confirmacao} />
                  </li>
                )
              })}
              {hojeItens.length > 8 && (
                <li className="ini__mais">
                  <button className="cf__link" onClick={() => ir('/agenda')}>
                    e mais {hojeItens.length - 8} na agenda
                  </button>
                </li>
              )}
            </ul>
          )}
        </section>
      )}
    </div>
  )
}

/* ── o cartão de número ───────────────────────────────────────────
   Anatomia fixa, com slots: glifo · rótulo · chevron · valor · frase.
   A frase é OBRIGATÓRIA e é uma instrução, não estatística — "3 em
   aberto" diz o que fazer; "vs mês passado" não diz nada às 8h. */
function Kpi({ rotulo, glifo, valor, frase, aoAbrir }: {
  rotulo: string; glifo: string; valor: string; frase: string; aoAbrir(): void
}) {
  return (
    <button className="cartao ini__kpi" onClick={aoAbrir}>
      <span className="ini__kpi-cab">
        <span className="ini__kpi-glifo" aria-hidden="true">{glifo}</span>
        <span className="ini__kpi-rotulo">{rotulo}</span>
        <span className="ini__kpi-seta" aria-hidden="true">›</span>
      </span>
      <span className="ini__kpi-valor">{valor}</span>
      <span className="ini__kpi-frase">{frase}</span>
    </button>
  )
}

/* Estado sempre com PALAVRA, nunca só cor — a distância entre
   "confirmado" (147°) e "em atendimento" (185°) é curta demais para
   quem tem deuteranopia. */
function EstadoDoDia({ status, confirmacao }: { status: string; confirmacao: string | null }) {
  if (status === 'EM_ATENDIMENTO') return <span className="selo selo--info">atendendo</span>
  if (status === 'NA_RECEPCAO') return <span className="selo selo--atencao">na recepção</span>
  if (status === 'CONCLUIDO') return <span className="selo selo--ok">concluído</span>
  if (status === 'FALTOU') return <span className="selo selo--erro">faltou</span>
  if (status.startsWith('CANCELADO')) return <span className="selo selo--neutro">cancelado</span>
  if (confirmacao === 'CONFIRMADO') return <span className="selo selo--ok">confirmado</span>
  return <span className="selo selo--neutro">agendado</span>
}
